import React, { useEffect } from 'react';

import { useJobProspectsState, useJobProspectsActions } from './JobProspectsContext';
import { getJobTitlePreferences, addJobTitlePreference, removeJobTitlePreference } from './restApi';
import PageSectionTitle from '../../Shared/UI/PageSectionTitle/components/PageSectionTitle';
import Button from '../../Shared/UI/Button/components/Button';
import styles from './JobProspectsSettings.module.scss';

const JobTitlePreferenceSettings = () => {
  const state = useJobProspectsState();
  const { onResponse, onError, setJobTitlePreferences, setAddingJobTitlePreference, setSelectedJobTitle } = useJobProspectsActions();

  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

  const jobTitlePreferences = state?.jobTitlePreferences || [];
  const addingJobTitlePreference = state?.addingJobTitlePreference || false;
  const selectedJobTitle = state?.selectedJobTitle || '';

  useEffect(() => {
    getJobTitlePreferences({
      onResponse: (data) => setJobTitlePreferences(data.jobTitlePreferences || []),
      onError,
    });
  }, []);

  const trimmedTitle = selectedJobTitle.trim();
  const isDuplicate = jobTitlePreferences.some(
    (preference) => preference.title.toLowerCase() === trimmedTitle.toLowerCase()
  );
  const addDisabled = !trimmedTitle || isDuplicate || addingJobTitlePreference;

  const handleAdd = () => {
    const title = selectedJobTitle.trim();
    if (!title) return;
    if (addingJobTitlePreference) return;
    if (jobTitlePreferences.some((preference) => preference.title.toLowerCase() === title.toLowerCase())) return;

    setAddingJobTitlePreference(true);
    addJobTitlePreference({
      body: { title, authenticityToken },
      onResponse,
      onError: (data) => onError({ ...data, addingJobTitlePreference: false }),
    });
  };

  const handleRemove = (preference) => {
    removeJobTitlePreference({
      id: preference.id,
      body: { authenticityToken },
      onResponse,
      onError,
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className={styles.titlePreferenceCard}>
      <PageSectionTitle text="Preferred Job Titles" />
      <p className={styles.description}>
        Add job titles you are looking for, such as Senior Scientist or Director of CMC. You can add more than one.
      </p>

      {jobTitlePreferences.length > 0 && (
        <div className={styles.titleChips}>
          {jobTitlePreferences.map(preference => (
            <div key={preference.id} className={styles.titleChip}>
              <span>{preference.title}</span>
              <button
                className={styles.titleChipRemove}
                onClick={() => handleRemove(preference)}
                aria-label={`Remove ${preference.title}`}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {jobTitlePreferences.length === 0 && (
        <p className={styles.noTitlesNote}>No preferred job titles set.</p>
      )}

      <div className={styles.titlePreferenceRow}>
        <input
          type="text"
          className={styles.titlePreferenceInput}
          value={selectedJobTitle}
          onChange={e => setSelectedJobTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. Senior Scientist"
          aria-label="Preferred job title"
        />
        <Button variant="primary" onClick={handleAdd} disabled={addDisabled}>
          {addingJobTitlePreference ? 'Adding...' : 'Add'}
        </Button>
      </div>
    </div>
  );
};

export default JobTitlePreferenceSettings;
